/**
 * useSelectionGroup.ts - Hook for building and validating selection groups
 *
 * Provides functionality for:
 * - Building SelectionGroup from selected node IDs
 * - Classifying edges as internal, incoming, or outgoing
 * - Validating group integrity and move operations
 *
 * Related:
 *   - types/selectionGroup.ts - Type definitions
 *   - hooks/useGroupDrag.ts - Uses this for drag operations
 *   - utils/groupReordering.ts - Move operations
 */

import { useCallback } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { SelectionGroup, BuildSelectionGroupResult, CanMoveGroupResult } from '../types/selectionGroup';
import { getChildNodeIds, getDescendantNodeIds } from '../utils/nodeReordering';
import { isTriggerNode } from '../types/typeGuards';
import { NODE_WIDTH, NODE_MIN_HEIGHT } from '../utils/layoutConstants';

/**
 * Options for the useSelectionGroup hook
 */
export interface UseSelectionGroupOptions {
	/** Current nodes in the canvas */
	nodes: Node<BaseNodeData>[];
	/** Current edges in the canvas */
	edges: Edge[];
}

/**
 * Return type for the useSelectionGroup hook
 */
export interface UseSelectionGroupReturn {
	/** Build a SelectionGroup from selected node IDs */
	buildSelectionGroup: (selectedNodeIds: string[]) => BuildSelectionGroupResult;

	/** Check if a group can be moved to a target position */
	canMoveGroupTo: (group: SelectionGroup, targetNodeId: string, position: 'before' | 'after') => CanMoveGroupResult;

	/** Expand selection to include all descendants of selected nodes */
	expandSelectionToIncludeDescendants: (nodeIds: string[]) => string[];
}

/**
 * Calculate bounding box of a set of nodes
 */
function calculateBounds(nodes: Node<BaseNodeData>[]): {
	x: number;
	y: number;
	width: number;
	height: number;
} {
	if (nodes.length === 0) {
		return { x: 0, y: 0, width: 0, height: 0 };
	}

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;

	for (const node of nodes) {
		const nodeWidth = node.measured?.width ?? NODE_WIDTH;
		const nodeHeight = node.measured?.height ?? NODE_MIN_HEIGHT;

		minX = Math.min(minX, node.position.x);
		maxX = Math.max(maxX, node.position.x + nodeWidth);
		minY = Math.min(minY, node.position.y);
		maxY = Math.max(maxY, node.position.y + nodeHeight);
	}

	return {
		x: minX,
		y: minY,
		width: maxX - minX,
		height: maxY - minY,
	};
}

/**
 * Classify edges based on whether their endpoints are in the selection.
 *
 * @param edges - All edges
 * @param selectedNodeIds - Set of selected node IDs
 * @returns Object with internal, incoming, and outgoing edge IDs
 */
function classifyEdges(
	edges: Edge[],
	selectedNodeIds: Set<string>
): {
	internalEdgeIds: string[];
	incomingEdgeIds: string[];
	outgoingEdgeIds: string[];
} {
	const internalEdgeIds: string[] = [];
	const incomingEdgeIds: string[] = [];
	const outgoingEdgeIds: string[] = [];

	for (const edge of edges) {
		const sourceInGroup = selectedNodeIds.has(edge.source);
		const targetInGroup = selectedNodeIds.has(edge.target);

		if (sourceInGroup && targetInGroup) {
			// Both endpoints in group = internal
			internalEdgeIds.push(edge.id);
		} else if (!sourceInGroup && targetInGroup) {
			// Only target in group = incoming
			incomingEdgeIds.push(edge.id);
		} else if (sourceInGroup && !targetInGroup) {
			// Only source in group = outgoing
			outgoingEdgeIds.push(edge.id);
		}
		// Neither in group = ignore
	}

	return { internalEdgeIds, incomingEdgeIds, outgoingEdgeIds };
}

/**
 * Find the entry node (node that receives incoming edges from outside)
 */
function findEntryNodeId(selectedNodeIds: Set<string>, incomingEdgeIds: string[], edges: Edge[]): string | null {
	// Find the node that has an incoming edge from outside the group
	for (const edgeId of incomingEdgeIds) {
		const edge = edges.find(e => e.id === edgeId);
		if (edge && selectedNodeIds.has(edge.target)) {
			return edge.target;
		}
	}
	return null;
}

/**
 * Find exit nodes (nodes with no children within the group)
 */
function findExitNodeIds(selectedNodeIds: Set<string>, edges: Edge[]): string[] {
	const exitNodeIds: string[] = [];

	for (const nodeId of selectedNodeIds) {
		const children = getChildNodeIds(nodeId, edges);
		// Check if all children are outside the group
		const hasChildInGroup = children.some(childId => selectedNodeIds.has(childId));
		if (!hasChildInGroup) {
			exitNodeIds.push(nodeId);
		}
	}

	return exitNodeIds;
}

/**
 * Hook for building and validating selection groups for drag operations.
 *
 * @example
 * ```typescript
 * const { buildSelectionGroup, canMoveGroupTo } = useSelectionGroup({
 *   nodes,
 *   edges,
 * });
 *
 * // Build group from selected nodes
 * const result = buildSelectionGroup(['node-1', 'node-2']);
 * if (result.success && result.group) {
 *   // Use group for drag operation
 *   startGroupDrag(result.group);
 * }
 *
 * // Validate move target
 * const canMove = canMoveGroupTo(group, 'target-node', 'after');
 * if (!canMove.valid) {
 *   console.log('Cannot move:', canMove.reason);
 * }
 * ```
 */
export function useSelectionGroup({ nodes, edges }: UseSelectionGroupOptions): UseSelectionGroupReturn {
	/**
	 * Build a SelectionGroup from selected node IDs
	 */
	const buildSelectionGroup = useCallback(
		(selectedNodeIds: string[]): BuildSelectionGroupResult => {
			const warnings: string[] = [];

			if (selectedNodeIds.length === 0) {
				return {
					success: false,
					group: null,
					warnings: [],
					error: 'No nodes selected',
				};
			}

			// Filter out trigger nodes
			const filteredNodeIds = selectedNodeIds.filter(id => {
				const node = nodes.find(n => n.id === id);
				if (!node) return false;
				if (isTriggerNode(node)) {
					warnings.push('Trigger nodes cannot be moved');
					return false;
				}
				return true;
			});

			if (filteredNodeIds.length === 0) {
				return {
					success: false,
					group: null,
					warnings,
					error: 'No movable nodes in selection',
				};
			}

			// Get selected nodes
			const selectedNodes = nodes.filter(n => filteredNodeIds.includes(n.id));
			const selectedIdSet = new Set(filteredNodeIds);

			// Classify edges
			const { internalEdgeIds, incomingEdgeIds, outgoingEdgeIds } = classifyEdges(edges, selectedIdSet);

			// Find entry and exit nodes
			let entryNodeId = findEntryNodeId(selectedIdSet, incomingEdgeIds, edges);

			// If no entry node found (disconnected selection), use the topmost node
			if (!entryNodeId && selectedNodes.length > 0) {
				const topNode = selectedNodes.reduce((top, node) => (node.position.y < top.position.y ? node : top));
				entryNodeId = topNode.id;
			}

			const exitNodeIds = findExitNodeIds(selectedIdSet, edges);

			// Calculate bounds
			const bounds = calculateBounds(selectedNodes);

			// Store original positions
			const originalPositions = new Map<string, { x: number; y: number }>();
			for (const node of selectedNodes) {
				originalPositions.set(node.id, { ...node.position });
			}

			const group: SelectionGroup = {
				nodeIds: filteredNodeIds,
				entryNodeId: entryNodeId!,
				exitNodeIds,
				bounds,
				originalPositions,
				internalEdgeIds,
				incomingEdgeIds,
				outgoingEdgeIds,
			};

			return {
				success: true,
				group,
				warnings,
			};
		},
		[nodes, edges]
	);

	/**
	 * Check if a group can be moved to a target position
	 */
	const canMoveGroupTo = useCallback(
		(group: SelectionGroup, targetNodeId: string, position: 'before' | 'after'): CanMoveGroupResult => {
			// Can't move to a node in the group
			if (group.nodeIds.includes(targetNodeId)) {
				return { valid: false, reason: 'Cannot move to a node within the selection' };
			}

			const targetNode = nodes.find(n => n.id === targetNodeId);
			if (!targetNode) {
				return { valid: false, reason: 'Target node not found' };
			}

			// Can't move before trigger
			if (position === 'before' && isTriggerNode(targetNode)) {
				return { valid: false, reason: 'Cannot move before trigger node' };
			}

			// Check for cycles - can't move to a descendant of any node in the group
			for (const nodeId of group.nodeIds) {
				const descendants = getDescendantNodeIds(nodeId, edges);
				if (descendants.includes(targetNodeId)) {
					return { valid: false, reason: 'Cannot create a cycle' };
				}
			}

			return { valid: true };
		},
		[nodes, edges]
	);

	/**
	 * Expand selection to include all descendants of selected nodes
	 */
	const expandSelectionToIncludeDescendants = useCallback(
		(nodeIds: string[]): string[] => {
			const expandedSet = new Set(nodeIds);

			for (const nodeId of nodeIds) {
				const descendants = getDescendantNodeIds(nodeId, edges);
				for (const descendant of descendants) {
					expandedSet.add(descendant);
				}
			}

			return Array.from(expandedSet);
		},
		[edges]
	);

	return {
		buildSelectionGroup,
		canMoveGroupTo,
		expandSelectionToIncludeDescendants,
	};
}
